import type { FocusTarget, FocusTargetValue, FocusTrap, Options } from "focus-trap";
import { createFocusTrap } from "focus-trap";
import { isFunction, isString } from "lodash-es";
import { v4 as uuid } from "uuid";
import type { Directive } from "vue";

/**
 * Traps the focus inside an element. Usage: `v-focus-trap`
 *
 * By default, the trap is removed when the user clicks outside the trapped element.
 * This can be disabled by passing `{ disableOnOutsideClick: false }` as a value.
 */
interface FocusTrapValue extends Options {
  disableOnOutsideClick?: boolean;
  rootEl?: HTMLElement;
  extraElements?: string[];
  paused?: boolean;
  // While true the trap is not activated, so the trap below it keeps working. Activates once false.
  deferred?: boolean;
  setReturnFocus?: ReturnFocusTarget | (() => ReturnFocusTarget | false);
}

type ReturnFocusTarget = string | HTMLElement;

export function focusTrap(): Directive<HTMLElement, FocusTrapValue> {
  const trapStack = new Array<FocusTrap>();
  const trapMap: Record<string, FocusTrap> = {};
  const deferredIds = new Set<string>();

  return {
    mounted(el, { value }) {
      // Make sure element has an ID, so we can track its focus trap
      el.id ||= uuid();

      // Clean up any existing focus trap for this element
      if (trapMap[el.id]) {
        trapMap[el.id].deactivate();
        delete trapMap[el.id];
      }

      const disableOnOutsideClick = value?.disableOnOutsideClick ?? true;

      const focusTrap = createFocusTrap(resolveElements(el, value), {
        initialFocus: false,
        trapStack,
        clickOutsideDeactivates: disableOnOutsideClick,
        ...(value || {}),
        setReturnFocus: returnFocus(el, value?.setReturnFocus),
        fallbackFocus: evaluateFallbackFocus(value?.fallbackFocus),
      });
      if (value?.deferred) {
        deferredIds.add(el.id);
      } else {
        focusTrap.activate();
      }

      if (value?.paused) {
        focusTrap.pause();
      }

      trapMap[el.id] = focusTrap;
    },
    updated(el, { value }) {
      const focusTrap = trapMap[el.id];

      if (!focusTrap || !value) return;

      if (deferredIds.has(el.id) && !value.deferred) {
        deferredIds.delete(el.id);
        focusTrap.activate();
      }

      if (!focusTrap.active) return;

      // Update container elements when extraElements changes
      if (value.extraElements) {
        focusTrap.updateContainerElements(resolveElements(el, value));
      }

      // Manage paused state
      if (value.paused && !focusTrap.paused) {
        focusTrap.pause();
      }

      if (!value.paused && focusTrap.paused) {
        focusTrap.unpause();
      }
    },
    unmounted(el) {
      const focusTrap = trapMap[el.id];

      focusTrap?.deactivate();
      delete trapMap[el.id];
      deferredIds.delete(el.id);
    },
  };
}

function resolveElements(el: HTMLElement, value?: FocusTrapValue) {
  const extraIds = value?.extraElements ?? [];
  return [value?.rootEl || el, ...extraIds.flatMap((id) => document.getElementById(id) ?? [])];
}

/**
 * Evaluates the fallback focus target. If it does not resolve to
 * a visible element, return "body" as the fallback.
 *
 * This ensures there will always be a valid focus target, even if
 * focus traps are unmounted in an unpredictable order (eg. when the user
 * navigates back in the browser history, with multiple traps active).
 * @internal exported for tests
 */
export function evaluateFallbackFocus(fallbackFocus?: FocusTarget): FocusTargetValue {
  let el = fallbackFocus;
  if (isFunction(el)) {
    el = el();
  }

  if (isString(el)) {
    el = document.querySelector<HTMLElement>(el) ?? undefined;
  }

  return el || "body";
}

// focus-trap returns focus asynchronously; by then something outside the trap may have taken it
function returnFocus(el: HTMLElement, target?: FocusTrapValue["setReturnFocus"]) {
  return () => {
    const active = document.activeElement;
    if (active && active !== document.body && !el.contains(active)) {
      return false;
    }
    const value = isFunction(target) ? target() : target;
    if (!value) {
      return false;
    }
    if (!isString(value)) {
      return value;
    }
    const node = document.querySelector<HTMLElement>(value);
    if (!node) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`Element for return focus not found '${value}'`);
      }
      return false;
    }
    return node;
  };
}
